import { create } from 'zustand';
import { getScheduledContentWithPlatforms } from '@/services/data/videoContent';
import { getAllPlatforms } from '@/services/data/socialConnections';
import type { Channel } from '@/services/business/calendar';

interface DisconnectedNetworksState {
  disconnectedNetworks: Record<string, Channel[]>;
  loading: Record<string, boolean>;
  errors: Record<string, Error | null>;
  // Counter that increments when social connections change, used to trigger refetch in other components
  socialConnectionsVersion: number;
  
  fetchDisconnectedNetworks: (userId: string) => Promise<void>;
  getDisconnectedNetworks: (userId: string) => Channel[];
  clearDisconnectedNetworks: (userId: string) => void;
  clearAllDisconnectedNetworks: () => void;
}

export const useDisconnectedNetworksStore = create<DisconnectedNetworksState>((set, get) => ({
  disconnectedNetworks: {},
  loading: {},
  errors: {},
  socialConnectionsVersion: 0,

  fetchDisconnectedNetworks: async (userId: string) => {
    const state = get();
    if (state.loading[userId]) {
      return;
    }

    set((state) => ({
      loading: { ...state.loading, [userId]: true },
      errors: { ...state.errors, [userId]: null },
    }));

    try {
      const scheduledContentResult = await getScheduledContentWithPlatforms(userId);
      
      if (!scheduledContentResult.success) {
        throw scheduledContentResult.error || new Error('Failed to fetch scheduled content');
      }

      const networksInSchedule = new Set<string>();
      scheduledContentResult.data?.forEach(publication => {
        if (publication.channel) {
          networksInSchedule.add(publication.channel.toLowerCase());
        }
      });

      const platformsResult = await getAllPlatforms(userId);
      
      if (!platformsResult.success || !platformsResult.data) {
        set((state) => ({
          disconnectedNetworks: { ...state.disconnectedNetworks, [userId]: [] },
          loading: { ...state.loading, [userId]: false },
          errors: { ...state.errors, [userId]: null },
        }));
        return;
      }

      const disconnected = platformsResult.data
        .filter(connection => 
          !connection.connected && 
          networksInSchedule.has(connection.platform.toLowerCase())
        )
        .map(connection => connection.platform as Channel);

      set((state) => ({
        disconnectedNetworks: { ...state.disconnectedNetworks, [userId]: disconnected },
        loading: { ...state.loading, [userId]: false },
        errors: { ...state.errors, [userId]: null },
        socialConnectionsVersion: state.socialConnectionsVersion + 1,
      }));
    } catch (error) {
      console.error('Error checking disconnected networks:', error);
      set((state) => ({
        disconnectedNetworks: { ...state.disconnectedNetworks, [userId]: [] },
        loading: { ...state.loading, [userId]: false },
        errors: { ...state.errors, [userId]: error as Error },
      }));
    }
  },

  getDisconnectedNetworks: (userId: string) => {
    return get().disconnectedNetworks[userId] ?? [];
  },

  clearDisconnectedNetworks: (userId: string) => {
    set((state) => {
      const { [userId]: _, ...disconnectedNetworks } = state.disconnectedNetworks;
      const { [userId]: __, ...loading } = state.loading;
      const { [userId]: ___, ...errors } = state.errors;
      return { disconnectedNetworks, loading, errors };
    });
  },

  clearAllDisconnectedNetworks: () => {
    set({ disconnectedNetworks: {}, loading: {}, errors: {} });
  },
}));

